import { AuthApi } from "@/services/auth-service"
import router from "@/router"
import useConvertDate from "@/composables/useConvertDate"
import { usePreferences } from "@/stores/preferences"
import { useSystemFeedback } from "@/stores/systemFeedback"
import { useUsers } from "@/stores/users"
import { useTenants } from "@/stores/tenants"
import { useModal } from "@/stores/modal"
import { useSlideOver } from "@/stores/slideOver"
import { clearLocalStorage } from "@/utils/localStorage"
import { defineStore } from "pinia"
import type { Login, MfaValidate, MfaType } from "@/types/auth"
import type { User } from "@/types/user"
import type { ApiError } from "@/types/api"

enum mfaType {
  "email" = 1,
  "qrCode" = 2
}

export const useAuth = defineStore("auth", {
  state: () => {
    return {
      loggedUser: {} as User,
      userIsBlocked: false,
      userMfaType: "" as MfaType,
      loginDigest: "",
      qtyErrorsMfaApp: 0,
      api: new AuthApi()
    }
  },

  getters: {
    loggedUserInitialsName: state =>
      state.loggedUser.first_name && state.loggedUser.last_name
        ? state.loggedUser.first_name[0] + state.loggedUser.last_name[0]
        : "...",

    hasPermission: state => {
      return (
        name: string,
        method: "get" | "post" | "put" | "patch" | "delete"
      ) =>
        state.loggedUser.user_group?.[0].group?.group_permissions.find(
          p =>
            p.permission.route_name === name && p.permission.method === method
        )
    }
  },

  actions: {
    async authLogin(credentials: Login) {
      this.userIsBlocked = false
      try {
        const response = await this.api.login(credentials)
        this.loggedUser.username = credentials.username
        this.userMfaType =
          mfaType[response.data.double_factor_type as mfaType] === "qrCode"
            ? "qrCode"
            : "email"
        this.loginDigest = response.data.digest
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async validateMfaCode(data: MfaValidate, type: MfaType) {
      try {
        const response = await this.api.validateMfa(data)
        if (response.data) {
          const useTenantsStore = useTenants()
          const userTenants = response.data.companies
          useTenantsStore.userTenants = userTenants
          localStorage.setItem("token", response.data.token)
          this.qtyErrorsMfaApp = 0
          if (userTenants.length === 1) {
            await useTenantsStore
              .setTenant({
                company_id: userTenants[0].company_id
              })
              .then(() => {
                router.push({
                  path: "/"
                })
              })
          }
        }
        return response
      } catch (error) {
        if (type === "qrCode") this.qtyErrorsMfaApp++
        if (this.qtyErrorsMfaApp >= 3 && type === "qrCode") {
          useSystemFeedback().setFeedback(
            {
              type: "warning",
              title: "mfa.mfaSync.title",
              text: "mfa.mfaSync.message"
            },
            0
          )
        } else {
          this.handleErrors(error as ApiError)
        }
      }
    },

    async resendCodeMfaMail() {
      try {
        return await this.api.resendCodeMfaMail({
          username: this.loggedUser.username
        })
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    setAccessToken(accessToken: string, refreshToken: string) {
      localStorage.setItem("access_token", accessToken)
      localStorage.setItem("refresh_token", refreshToken)
    },

    hasLocalStorageToken() {
      const token = localStorage.getItem("access_token")
      return token && token.length > 0
    },

    async logout() {
      const accessToken = localStorage.getItem("access_token")
      const refreshToken = localStorage.getItem("refresh_token")
      useModal().closeAll()
      useSlideOver().closeAllSlides()
      if (accessToken && refreshToken) {
        await this.api.logout().finally(() => {
          this.clearAuth()
        })
      } else {
        this.clearAuth()
      }
    },

    clearAuth() {
      this.$reset()
      clearLocalStorage()
      router.push({ name: "Login" })
    },

    async getLoggedUser() {
      const usersStore = useUsers()
      const preferencesStore = usePreferences()
      const { pcTimeZone } = useConvertDate()
      let timeZone = pcTimeZone

      try {
        const response = await usersStore.getUserByToken()
        const user = response.data
        this.loggedUser = user
        preferencesStore.setLanguage(this.loggedUser.language)
        if (!this.loggedUser.machine_time_zone && this.loggedUser.time_zone) {
          timeZone = this.loggedUser.time_zone
        }
        preferencesStore.setTimeZone(timeZone)
        const useTenantsStore = useTenants()
        useTenantsStore.getUserTenants()
      } catch (error) {
        this.handleErrors(error as ApiError)
        router.push({ name: "Logout" })
      }
    },

    handleErrors(error: ApiError) {
      const errorMessage = error?.response
        ? `backend.${error.response.data.detail}`
        : "requestError.default"
      let timeoutSystemFeedback = 5000
      if (errorMessage === "backend.api008") {
        timeoutSystemFeedback = 0
        this.userIsBlocked = true
      }
      useSystemFeedback().setFeedback(
        { type: "danger", text: errorMessage },
        timeoutSystemFeedback
      )
    }
  }
})
